import React, { useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import PageStack from '../../components/PageStack';
import DashboardTable from '../../components/DashboardTable';
import { LAYOUT } from '../../theme/layoutTokens';
import { supplierProductsApi, SupplierProductSearchParams } from '../../api/supplierProducts.api';
import { categoriesApi } from '../../api/categories.api';
import { clientsApi } from '../../api/clients.api';
import { SupplierProduct } from '../../types/supplierProduct';
import { Category } from '../../types/category';
import { Client, ClientType } from '../../types/client';
import { debounce } from 'lodash';

const FILTER_ALL = '__all__';

const SupplierProductList: React.FC = () => {
  const [products, setProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryReference, setSelectedCategoryReference] = useState<string | undefined>();
  const [selectedSupplierReference, setSelectedSupplierReference] = useState<string | undefined>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Client[]>([]);
  const [expandedReference, setExpandedReference] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadCategories();
    loadSuppliers();
  }, []);

  const loadProducts = async (filters: SupplierProductSearchParams) => {
    try {
      setLoading(true);
      const response = await supplierProductsApi.getAll(filters);
      setProducts(response.data);
      setCurrentPage(response.page);
      setTotalPages(response.lastPage);
      setTotalItems(response.total);
    } catch (error) {
      console.error('Error loading supplier products:', error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = debounce((filters: SupplierProductSearchParams) => {
    loadProducts(filters);
  }, 300);

  useEffect(() => {
    const filters: SupplierProductSearchParams = {
      categoryReference: selectedCategoryReference,
      supplierClientReference: selectedSupplierReference,
      page: currentPage,
      limit: itemsPerPage,
    };
    if (searchTerm && searchTerm.length >= 2) {
      filters.search = searchTerm;
    }
    debouncedSearch(filters);
  }, [searchTerm, selectedCategoryReference, selectedSupplierReference, currentPage, itemsPerPage]);

  const loadCategories = async () => {
    try {
      const data = await categoriesApi.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await clientsApi.getAll(1, 100, ClientType.SUPPLIER);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const handleDelete = async (reference: string) => {
    if (!window.confirm('¿Eliminar este producto de proveedor?')) {
      return;
    }
    try {
      await supplierProductsApi.delete(reference);
      const filters: SupplierProductSearchParams = {
        categoryReference: selectedCategoryReference,
        supplierClientReference: selectedSupplierReference,
        page: currentPage,
        limit: itemsPerPage,
      };
      if (searchTerm.length >= 2) {
        filters.search = searchTerm;
      }
      await loadProducts(filters);
    } catch (error) {
      console.error('Error deleting supplier product:', error);
    }
  };

  const toggleExpanded = (reference: string) => {
    setExpandedReference((prev) => (prev === reference ? null : reference));
  };

  if (loading && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader title="Productos de proveedores" />

      <Sheet variant="outlined" sx={{ p: LAYOUT.cardPadding, borderRadius: 'md', boxShadow: 'sm' }}>
        <Grid container spacing={LAYOUT.gridSpacing}>
          <Grid xs={12} md={4}>
            <FormControl>
              <FormLabel>Buscar por nombre</FormLabel>
              <Input
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Mínimo 2 caracteres"
                startDecorator={<SearchRoundedIcon />}
              />
            </FormControl>
          </Grid>
          <Grid xs={12} md={4}>
            <FormControl>
              <FormLabel>Categoría</FormLabel>
              <Select
                value={selectedCategoryReference ?? FILTER_ALL}
                onChange={(_, value) => {
                  const v = value as string;
                  setSelectedCategoryReference(v === FILTER_ALL ? undefined : v);
                  setCurrentPage(1);
                }}
              >
                <Option value={FILTER_ALL}>Todas</Option>
                {categories.map((category) => (
                  <Option key={category.reference} value={category.reference}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} md={4}>
            <FormControl>
              <FormLabel>Proveedor</FormLabel>
              <Select
                value={selectedSupplierReference ?? FILTER_ALL}
                onChange={(_, value) => {
                  const v = value as string;
                  setSelectedSupplierReference(v === FILTER_ALL ? undefined : v);
                  setCurrentPage(1);
                }}
              >
                <Option value={FILTER_ALL}>Todos</Option>
                {suppliers.map((supplier) => (
                  <Option key={supplier.reference} value={supplier.reference}>
                    {supplier.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Sheet>

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        {products.length === 0 ? (
          <Typography level="body-sm" sx={{ py: 4, textAlign: 'center', color: 'neutral.500' }}>
            No hay productos de proveedores cargados.
          </Typography>
        ) : (
          <>
            <DashboardTable stickyHeader>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Categoría</th>
                  <th>Proveedores</th>
                  <th>Mejor precio</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <React.Fragment key={product.reference}>
                    <tr>
                      <td>
                        <Typography level="body-sm" fontWeight="md">
                          {product.name}
                        </Typography>
                        {product.description ? (
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                            {product.description}
                          </Typography>
                        ) : null}
                      </td>
                      <td>{product.categoryName ?? '—'}</td>
                      <td>
                        <Chip size="sm" variant="soft" color="warning">
                          {product.offerCount}
                        </Chip>
                      </td>
                      <td>
                        {product.bestPrice != null ? `$${product.bestPrice.toFixed(2)}` : '—'}
                      </td>
                      <td>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => toggleExpanded(product.reference)}
                          >
                            {expandedReference === product.reference ? (
                              <ExpandLessRoundedIcon />
                            ) : (
                              <ExpandMoreRoundedIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(product.reference)}
                          >
                            <DeleteRoundedIcon />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                    {expandedReference === product.reference ? (
                      <tr>
                        <td colSpan={5}>
                          <Stack spacing={1} sx={{ py: 1 }}>
                            {product.offers.map((offer) => (
                              <Stack
                                key={offer.reference}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{
                                  px: 2,
                                  py: 1,
                                  bgcolor: 'background.level1',
                                  borderRadius: 'sm',
                                }}
                              >
                                <Typography level="body-sm" sx={{ flex: 1 }}>
                                  {offer.supplierClientName}
                                </Typography>
                                <Typography level="body-sm" fontWeight="md">
                                  ${offer.price.toFixed(2)}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </DashboardTable>

            <Box
              sx={{
                ...LAYOUT.tableFooterPadding,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: LAYOUT.stackTight,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography level="body-sm">
                Mostrando {products.length} de {totalItems} resultados
              </Typography>
              {totalPages > 1 ? (
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    startDecorator={<ChevronLeftRoundedIcon />}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    endDecorator={<ChevronRightRoundedIcon />}
                  >
                    Siguiente
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </>
        )}
      </Sheet>
    </PageStack>
  );
};

export default SupplierProductList;
